import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import {
  TableSortLabel,
  Box,
  Typography,
  MenuItem,
  Button,
  Menu,
  FormControlLabel,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import { Page, Row } from '../../util/Page';
import TextDataCell from './TextDataCell';
import TimeDataCell from './TimeDataCell';
import LinearDataCell from './LinearDataCell';
import ButtonDataCell from './ButtonDataCell';
import IdentityDataCell from './IdentityDataCell';
import BadgesDataCell from './BadgesDataCell';
import { setPage } from '../../../redux/actions/staging';

// This is a very dirty copy of StagingDataTable, copied and not DRYed because we intend to move away from these relatively soon. If that decision is reneged, be very careful about edits to this or StagingDataTable without first aligning them properly
const StagingDisplayTable = ({
  title,
  columns,
  filteredData,
  rowIdKey,
  page,
  handleChangePage,
}) => {
  const rowCount = filteredData.length;
  const [sortColumns, setSortColumns] = React.useState({});
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [hiddenColumns, setHiddenColumns] = React.useState(
    columns.reduce((acc, column) => {
      acc[column.id] = column.defaultHidden;
      return acc;
    }, {})
  );
  const [anchorEl, setAnchorEl] = React.useState(null);
  const columnsMenuOpen = Boolean(anchorEl);

  const toggleColumnsMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColumnToggle = (column) => {
    setHiddenColumns((prevHiddenColumns) => {
      // Toggle the hidden state of the specified column
      const isCurrentlyHidden = !!prevHiddenColumns[column.id];

      return {
        ...prevHiddenColumns,
        [column.id]: !isCurrentlyHidden,
      };
    });
  };

  const handleColumnsMenuClose = () => {
    setAnchorEl(null);
  };

  // Temporary sorting code, will be moved to backend
  const sortedData = useMemo(() => {
    let result = filteredData;
    if (columns.map((c) => c.id).some((c) => Object.keys(sortColumns).includes(c))) {
      const columnDef = columns.filter((c) => Object.keys(sortColumns).includes(c.id))[0];
      result = result.sort(columnDef.sortFunction);
      if (sortColumns[columnDef.id].direction !== 'asc') result = result.reverse();
    }
    return result;
  }, [filteredData, columns, sortColumns]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    handleChangePage(event, 0);
  };

  const handleSort = (column) => () => {
    let newSortColumns = { ...sortColumns }; // Create a shallow copy for react to detect changes.
    if (column.id in sortColumns) {
      // Reverse
      newSortColumns[column.id].direction =
        newSortColumns[column.id].direction === 'asc' ? 'desc' : 'asc';
      setSortColumns(newSortColumns);
      return;
    }
    // Delete this code when enabling multi-column sorting
    newSortColumns = {};
    newSortColumns[column.id] = { direction: 'asc', order: 1 };
    setSortColumns(newSortColumns);
  };

  if (filteredData.length <= 0) {
    return (
      <Page>
        <Row>There are no results to show.</Row>
      </Page>
    );
  }

  return (
    <Page>
      <Row>
        <TableContainer style={{ width: '100%' }}>
          <Table stickyHeader aria-label="points table" size="small">
            <TableHead>
              <Box display="flex" alignItems="center" mb={2} mt={2}>
                <Typography variant="h5" component="h2">
                  {title}
                </Typography>
                <Typography variant="body1" color="textSecondary" style={{ marginLeft: '8px' }}>
                  ({rowCount})
                </Typography>
              </Box>
              {columns.some((c) => c.hideable) ? (
                <TableRow>
                  <TableCell sx={{ borderBottom: 'none', p: 0 }}>
                    <Button
                      color="info"
                      aria-controls={columnsMenuOpen ? 'columns-visibility-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={columnsMenuOpen ? 'true' : undefined}
                      startIcon={<ViewColumnIcon />}
                      sx={{ py: 0, px: 2 }}
                      onClick={toggleColumnsMenu}
                    >
                      Columns
                    </Button>
                    <Menu
                      id="columns-visibility-menu"
                      anchorEl={anchorEl}
                      open={columnsMenuOpen}
                      onClose={handleColumnsMenuClose}
                    >
                      {columns
                        .filter((column) => column.hideable)
                        .map((column) => (
                          <MenuItem key={`${column.id}:hideToggle`}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  color="info"
                                  checked={!hiddenColumns[column.id]}
                                  onChange={() => handleColumnToggle(column)}
                                  inputProps={{ 'aria-label': 'controlled' }}
                                />
                              }
                              label={column.label}
                            />
                          </MenuItem>
                        ))}
                    </Menu>
                  </TableCell>
                </TableRow>
              ) : null}
              <TableRow>
                {columns
                  .filter((column) => !column.hideable || !hiddenColumns[column.id])
                  .map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      sortDirection={
                        column.id in sortColumns ? sortColumns[column.id].direction : false
                      }
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active={column.id in sortColumns}
                          direction={sortColumns[column.id]?.direction || 'desc'}
                          onClick={handleSort(column)}
                        >
                          <Typography>
                            <b>{column.label}</b>
                            {column.id in sortColumns ? (
                              <Box
                                component="span"
                                style={{ verticalAlign: 'super', fontSize: '0.7em' }}
                              >
                                {sortColumns[column.id].order}
                              </Box>
                            ) : null}
                          </Typography>
                        </TableSortLabel>
                      ) : (
                        <b>{column.label}</b>
                      )}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <TableRow
                  sx={{
                    transition: 'box-shadow 0.1s ease-in-out', // Smooth transition for the shadow
                    '&:hover': {
                      boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.3)',
                    },
                  }}
                  role="checkbox"
                  tabIndex={-1}
                  key={row[rowIdKey]}
                >
                  {columns
                    .filter((column) => !column.hideable || !hiddenColumns[column.id])
                    .map((column) => {
                      switch (column.type) {
                        case 'badges':
                          return <BadgesDataCell column={column} value={row} key={column.id} />;
                        case 'identity':
                          return <IdentityDataCell column={column} value={row} key={column.id} />;
                        case 'linear':
                          return <LinearDataCell column={column} value={row} key={column.id} />;
                        case 'button':
                          return (
                            <ButtonDataCell
                              column={column}
                              value={row}
                              key={column.id}
                              valueId={row[rowIdKey]}
                            />
                          );
                        case 'time':
                          return <TimeDataCell column={column} value={row} key={column.id} />;
                        default:
                          return <TextDataCell column={column} value={row} key={column.id} />;
                      }
                    })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Row>
      <Row>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Row>
    </Page>
  );
};

const mapStateToProps = (state) => {
  /* It's gross that we're keeping the page in redux, but this is the path of least resistance when we push pagination logic to the queries
   * If we want to revert this, look at the commit for this comment as this change was done as one atomic unit */
  const {
    entities: {
      staging: { page },
    },
  } = state;
  return { page };
};

const mapDispatchToProps = (dispatch) => ({
  handleChangePage: (event, newPage) => dispatch(setPage(newPage)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StagingDisplayTable);
